import { CustomerDTO } from "@/modules/clients/dtos";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { HandCoins } from "lucide-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BoardAction } from "../dtos";
import { useWorkflowBoard } from "../hooks/use-workflow-board.hook";
import {
  clearBoardFocus,
  useBoardFocusRequest,
} from "../utils/board-focus-store";
import CaseHeaderActions from "./case-header-actions";
import CaseStatusPills from "./case-status-pills";
import PhaseStepper from "./phase-stepper";
import CaseSwitcher from "./case-switcher";
import StartAnotherApplicationButton from "./start-another-application-button";
import StartWorkflowCard from "./start-workflow-card";
import StepCard from "./step-card";
import {PaymentPlanUpdate} from './payment-plan-update';

/**
 * The "Nawy Now" lead tab: the loan-process board straight from workflow-manager
 * (numbered phase stepper → expandable step cards). With no case yet, the empty state
 * offers "Start loan process" for the current lead.
 *
 * §BannerAboveTabs: the next-action / flow-blocked banner and its review modal live ABOVE the lead
 * page's tab bar (NawyNowStatusBanner) so they are visible from every tab. Picking an action there
 * publishes a focus request this board consumes below.
 */
const NawyNowTab = ({
  client,
  clientReady,
  leadUid,
  journeyUid,
  customerUid,
  isNawyNowLead,
}: {
  client?: CustomerDTO;
  /** customer lookup settled — gates the board fetch so it runs once, with phones */
  clientReady?: boolean;
  leadUid?: string;
  journeyUid?: string;
  customerUid?: string;
  /** §NawyNowGate: only a "nawy now" lead may START a flow — an existing case still renders. */
  isNawyNowLead?: boolean;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const {
    isLoading,
    isError,
    refetch,
    hasCase,
    board,
    caseUid,
    participantUid,
    cases,
    selectedCaseUid,
    selectCase,
    isSwitching,
  } = useWorkflowBoard({ client, clientReady, journeyUid });

  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
  const [expandedStepUid, setExpandedStepUid] = useState<string | null>(null);
  // §Multi-application: another application has its own phases and steps — drop the previous one's
  // phase pick and open card rather than carrying them across.
  useEffect(() => {
    setSelectedPhase(null);
    setExpandedStepUid(null);
  }, [selectedCaseUid]);
  const startCustomerUid = customerUid ?? client?.uid;
  // §Multi-application: starting ANOTHER application is reserved for holders of the UMS passport permission
  // `nawy-now-can-add-multiple-apps` (workflow-manager enforces the same resource). `userHasPermission`, not
  // `<PermissionGuard>`, so the button shows exactly when WM would accept the start (same as Terminate).
  const { permissions } = useContext(NawyPassportContext);
  const mayAddMultiple = userHasPermission(
    { name: PermissionsEnum.NAWY_NOW_ADD_MULTIPLE_APPS, level: PermissionLevel.WRITE },
    permissions
  );
  const canStartAnother = !!(mayAddMultiple && isNawyNowLead && leadUid && startCustomerUid && cases.length >= 1);

  // §FlowBusy (live bite, 2026-08-05): WM-style thin progress bar under the header whenever
  // ANYTHING in the flow is in flight — a board/step refetch or any write (autosave, upload,
  // submit). Scoped to the nawy-now query tree so lead-page traffic doesn't flash it.
  const flowFetching = useIsFetching({ queryKey: ["nawy-now"] });
  // §FlowBusy-scope (2026-08-08): `useIsMutating()` unfiltered counts EVERY mutation in the app — including
  // the per-field autosave, which fires a second after the agent stops typing. Combined with the freeze
  // below that disabled every action button in the board for the length of each autosave, which is the same
  // defect just fixed one level down in step-form's `busy`. Quiet (background) saves are excluded here so
  // both layers agree on what "the user is waiting on something" means.
  const flowMutating = useIsMutating({
    predicate: (mutation) =>
      !(mutation.state.variables as { quiet?: boolean } | undefined)?.quiet,
  });
  /** Anything in flight — drives the progress bar, which is purely informational. */
  const flowActive = flowFetching > 0 || flowMutating > 0;
  /**
   * Only USER-INITIATED work freezes the controls. The original §FlowBusy ask (2026-08-05) was to stop a
   * double-fired Submit/Upload/Confirm landing on state that is about to change — a real hazard while a
   * mutation is in flight. Background refetches were rare then; they are now routine (SSE invalidations,
   * the poll backstop), and freezing on those turns the board unusable at exactly the wrong moments.
   */
  const flowBusy = flowMutating > 0;

  const phases = useMemo(() => board?.phases ?? [], [board]);
  const activePhaseName = useMemo(() => {
    if (selectedPhase && phases.some((phase) => phase.name === selectedPhase)) {
      return selectedPhase;
    }
    return (
      (
        phases.find((phase) => phase.uiStatus === "CURRENT") ??
        phases.find((phase) => phase.uiStatus !== "DONE") ??
        phases[0]
      )?.name ?? null
    );
  }, [selectedPhase, phases]);
  const activePhase =
    phases.find((phase) => phase.name === activePhaseName) ?? null;
  const activeIsFirst =
    phases.length > 0 && phases[0]?.name === activePhaseName;
  // design: the Container squares the corner the SELECTED tab sits on — top-left when the first
  // tab is active, top-RIGHT when the last one is (both variants ship in the Figma frames; only
  // the first-tab case was implemented, so the last tab used to float off a rounded corner)
  const activeIsLast =
    phases.length > 0 && phases[phases.length - 1]?.name === activePhaseName;

  // Hoisted out of the step map — inline it nested five function levels deep (Sonar S2004).
  const toggleStep = (stepUid: string) =>
    setExpandedStepUid((previous) => (previous === stepUid ? null : stepUid));

  const revealStep = (phaseName: string, stepUid: string) => {
    setSelectedPhase(phaseName);
    setExpandedStepUid(stepUid);
    // The card may not have painted yet — a fresh board load, or the mobile full-screen sheet still
    // mounting/animating — so a single fixed delay misses. Retry briefly until the card exists; the
    // expansion state above is already asserted, the scroll just needs the element in the DOM.
    let attempts = 0;
    const tryScroll = () => {
      const element = document.getElementById(`nawy-now-step-${stepUid}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (attempts < 20) {
        attempts += 1;
        setTimeout(tryScroll, 150);
      }
    };
    setTimeout(tryScroll, 150);
  };

  const jumpTo = (action: BoardAction) =>
    revealStep(action.phaseName, action.stepUid);

  // §BannerAboveTabs: the above-the-tabs banner asked for a step — reveal it, then clear the
  // request so it fires exactly once (a re-mount must not replay a stale jump).
  const focusRequest = useBoardFocusRequest();
  useEffect(() => {
    if (!focusRequest) return;
    jumpTo(focusRequest);
    clearBoardFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  // §Agent-link: a workflow-manager task-assigned notification deep-links the agent here with
  // `?tab=nawy_now&stepUid=<uid>`. Once the board is in, reveal that step exactly like a banner
  // focus request, then strip the param so tab switches and reloads don't replay the jump. An
  // unknown uid (stale link, step now hidden) just drops the param and leaves the default view.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkStepUid = searchParams.get("stepUid");
  useEffect(() => {
    // §Multi-application: only reveal on the SELECTED application's own board — while switching (a deep
    // link's `caseUid` names a case other than the one keepPreviousData still shows) the step would not be
    // found and the param would be stripped before the right board arrives.
    if (!deepLinkStepUid || !board || isLoading || isSwitching || caseUid !== selectedCaseUid) return;
    const phase = board.phases.find((boardPhase) =>
      boardPhase.steps.some((s) => s.step.uid === deepLinkStepUid)
    );
    if (phase) revealStep(phase.name, deepLinkStepUid);
    searchParams.delete("stepUid");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkStepUid, board, isLoading, isSwitching, caseUid, selectedCaseUid]);

  if (isLoading) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Skeleton variant="rounded" width="100%" height={64} />
        <Skeleton variant="rounded" width="100%" height={320} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack spacing={1.5} alignItems="center" sx={{ py: 8 }}>
        <Typography
          sx={{ fontSize: 14, lineHeight: "20px", color: colors.semantic.text.extension.negative }}
        >
          Couldn&apos;t load the loan process.
        </Typography>
        <Button
          variant="outlined"
          onClick={refetch}
          sx={{ textTransform: "none" }}
        >
          Retry
        </Button>
      </Stack>
    );
  }

  if (!hasCase || !board) {
    // §NawyNowGate: never offer "Start" on a non-nawy-now lead (deep links bypass the hidden tab;
    // crm-backend rejects it server-side too) — fall through to the quiet empty state instead.
    if (client && leadUid && isNawyNowLead) {
      return <StartWorkflowCard client={client} leadUid={leadUid} />;
    }
    return (
      <Stack spacing={1.5} alignItems="center" sx={{ py: 10 }}>
        <HandCoins size={40} color={colors.semantic.text.tertiary} />
        <Typography sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 600 }}>
          No active loan process
        </Typography>
        <Typography variant="body2" color={colors.semantic.text.secondary}>
          This client has no Nawy Now application yet.
        </Typography>
      </Stack>
    );
  }

  return (
    // design: the board sits 16px off the screen edge (Figma 12107:50124 is 343 wide in a 375
    // frame). On mobile the FullPageModal already contributes 8px, so 8 here totals the 16.
    <Box sx={{ p: { mobile: 1, tablet: 2 }, display: "flex", flexDirection: "column", gap: 2 }}>
      {caseUid && !isSwitching && (
        <PaymentPlanUpdate key={caseUid} caseUid={caseUid} refreshKey={board} onChanged={refetch} />
      )}
      <Paper
        elevation={0}
        sx={{
          borderRadius: theme.nawy.radius.xl,
          // §FolderTabs (design 12107:50124): the "Loan process" block is a white SURFACE, not a
          // bordered box — the only border in the group belongs to the Container under the tabs.
          // The old outer border + inner padding drew a second box around the tabs and inset them
          // 8/12px, so the folder tabs never met the container edge the way the design has them.
          //
          // §StickyHeader: NO `overflow: hidden` here, even though the design frame is
          // overflow-clip. Any clipping ancestor becomes the sticky header's scroll container, and
          // since this box never scrolls the header stops sticking. Nothing needs the clip anyway —
          // the first/last tab carry the same 12px top radius as this surface and the Container
          // carries the bottom pair, so every corner is already drawn by the child that owns it.
          // §SurfaceInset: the desktop frame (12107:48053) puts Title, Folder Tabs and Container all
          // at x=12 inside a 696-wide surface — a 12px pad. The MOBILE frame (12107:50124) is the
          // opposite: block 343, tabs 343, container 343, i.e. dead flush. So the inset is real but
          // breakpoint-specific, not a constant.
          p: { mobile: 0, tablet: 1.5 },
          // §FlowBusy (product ask, 2026-08-05): while ANYTHING is in flight, every action button
          // in the board freezes — a double-fired Submit/Upload/Confirm during a refetch acts on
          // state that is about to change under it. Scoped to real action controls (Button /
          // IconButton): the phase tabs and card expand/collapse are plain ButtonBase and stay
          // usable, so the agent can still look around while a save settles.
          ...(flowBusy && {
            "& .MuiButton-root, & .MuiIconButton-root": {
              pointerEvents: "none",
              opacity: 0.55,
            },
          }),
        }}
      >
        {/* §StickyHeader (live bite, 2026-08-05): the title row AND the phase stepper pin to the
            tab's scroller — only the title used to stick, so the stepper (the actual navigation)
            scrolled away on long boards. The Paper carries no padding now, so the pinned block
            spans the surface edge-to-edge and needs no negative-margin compensation. */}
        <Box
          sx={{
            position: "sticky",
            top: -1,
            zIndex: 2,
            backgroundColor: colors.semantic.layer.primary,
            // Re-span the Paper's inset so the pinned white ground reaches the surface edges —
            // otherwise rows scrolling underneath peek past it at the sides — then push the content
            // back onto the inset. No-ops on mobile, where the Paper has no padding to re-span.
            mx: { mobile: 0, tablet: -1.5 },
            px: { mobile: 0, tablet: 1.5 },
            mt: { mobile: 0, tablet: -1.5 },
            pt: { mobile: 0, tablet: 1.5 },
          }}
        >
        {/* The surface has no padding of its own, so the title row carries the design's 16px inset —
            the folder tabs below it stay deliberately flush to the surface edge.

            §HeaderStripped (product ask, 2026-08-10): the case-status chip and the "Agent: …" line
            are gone. The status is already told by the phase tabs and the step pills underneath,
            and the owning agent is on the lead header a few hundred pixels up — both were repeating
            what the page says elsewhere, on the one row that has to stay readable at 390px. The
            board still serves `statusBadge` / `caseStatus` / `agent`; nothing but this row changed.
            §CoApplicantEntry: adding a guarantor lives in the Co-applicants card, not here. */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            // design: Title frame at x=12 with its text 4px in — 16px from the surface edge. On
            // desktop the Paper already supplies the 12, so only the 4 belongs here.
            px: { mobile: 2, tablet: 0.5 },
            pt: { mobile: 2, tablet: 0 },
            mb: 1.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            alignItems="center"
            sx={{ flexWrap: "wrap", rowGap: 0.5, minWidth: 0 }}
          >
            <Typography
              sx={{
                fontSize: 16,
                lineHeight: "22px",
                fontWeight: 600,
                color: colors.semantic.text.primary,
              }}
            >
              Loan process
            </Typography>
            {/* §CaseVerdict (QA prod, 2026-08-16): the WHOLE-application status, mirroring the WM
                case header ("⊘ Cancelled" + "Credit Declined"). §HeaderStripped removed this row's
                chip because "the phase tabs already tell the status" — which broke exactly on a
                CLOSED case: an application killed by a disqualifying i-Score showed nothing but a
                green "Onboarding — Done". The funnel badge (WM label + color) always renders when
                present; the lifecycle chip joins it only when the case is terminal. */}
            {!isSwitching && <CaseStatusPills board={board} />}
          </Stack>
          {/* §Terminate + §Clone-steps: the header's right side (deliberately emptied by
              §HeaderStripped) hosts the case-level actions on the SELECTED application. */}
          {caseUid && !isSwitching && (
            <CaseHeaderActions caseUid={caseUid} cases={cases} caseStatus={board?.caseStatus} />
          )}
        </Stack>
        {/* §Multi-application: a lead may run several applications — the switcher lists them (it renders
            nothing for one), and "Start another application" opens the next one and selects it. */}
        {(cases.length > 1 || canStartAnother) && (
          // §MobileQA: at 390px the button beside the chips left room for ONE chip, hiding the second
          // application — on mobile the chips get their own full-width row and the button sits under them.
          <Stack
            direction={{ mobile: "column", tablet: "row" }}
            alignItems={{ mobile: "stretch", tablet: "center" }}
            justifyContent="space-between"
            gap={1}
            sx={{ px: { mobile: 2, tablet: 0.5 }, mb: 1.5, minWidth: 0 }}
          >
            <CaseSwitcher cases={cases} selectedUid={selectedCaseUid} onSelect={selectCase} />
            {canStartAnother && startCustomerUid && leadUid && (
              <Box sx={{ ml: { mobile: 0, tablet: "auto" }, flexShrink: 0, alignSelf: { mobile: "flex-start", tablet: "center" } }}>
                <StartAnotherApplicationButton
                  customerUid={startCustomerUid}
                  leadUid={leadUid}
                  cases={cases}
                  onStarted={selectCase}
                />
              </Box>
            )}
          </Stack>
        )}
        {!isSwitching && (
          <PhaseStepper
            phases={phases}
            selected={activePhaseName}
            onSelect={setSelectedPhase}
          />
        )}
        {/* §FlowBusy: WM-style thin bar pinned to the header's bottom edge while anything runs —
            including background refetches, which show progress but no longer disable anything. */}
        {flowActive && (
          <LinearProgress
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -1,
              height: 3,
              zIndex: 3,
              backgroundColor: "#E6EFF5",
              "& .MuiLinearProgress-bar": { backgroundColor: "#015C9A" },
            }}
          />
        )}
        </Box>
        <Box
          sx={{
            border: `1px solid ${colors.semantic.border.secondary}`,
            borderRadius: theme.nawy.radius.xl,
            // §Case-hold: the whole application is frozen — every action control on the board is inert (the
            // same sx as §FlowBusy). Scoped to the steps container, NOT the Paper, so the header's Terminate
            // stays usable (terminating a held application is legitimate — WM clears the hold).
            ...(board?.onHold && {
              "& .MuiButton-root, & .MuiIconButton-root": {
                pointerEvents: "none",
                opacity: 0.55,
              },
            }),
            // square only the corner the active tab sits on — left when it's the first tab,
            // right when it's the last (design ships both variants)
            borderTopLeftRadius: activeIsFirst ? 0 : theme.nawy.radius.xl,
            borderTopRightRadius: activeIsLast ? 0 : theme.nawy.radius.xl,
            // design 12107:50132: Container is px md (16) / py sm (12) on EVERY breakpoint —
            // mobile was squeezed to 6px to claw back width while the Paper still added its own
            // padding; with the surface padding gone the design value fits at 375px.
            px: 2,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            // design 12107:48053: consecutive Expandable widgets sit 8px apart (the second starts at
            // y=278 against the first's height of 270) — NOT the md/16 the mobile frame's Widgets
            // column uses. 16 here was the real source of the "too much white space": it doubled the
            // separation while the padding INSIDE each card was what actually wanted the room.
            gap: 1,
          }}
        >
          {/* §Sections: co-applicant step blocks (joint / guarantor) get a labeled divider;
              a single-section phase (applicant only) renders flat, exactly as before */}
          {/* §Multi-application: the previous application's board is still in hand while the selected one
              loads — never paint its steps under the new selection. */}
          {isSwitching && (
            <Stack spacing={1} data-testid="case-switching">
              <Skeleton variant="rounded" width="100%" height={56} />
              <Skeleton variant="rounded" width="100%" height={56} />
              <Skeleton variant="rounded" width="100%" height={56} />
            </Stack>
          )}
          {!isSwitching && activePhase?.sections.map((section) => (
            <Box
              key={section.key}
              // same xs = 8px rhythm inside a participant block
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              {activePhase.sections.length > 1 && (
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ mt: section.key === "APPLICANT" ? 0 : 0.5, minWidth: 0 }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      lineHeight: "16px",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: colors.semantic.text.secondary,
                      // §MobileQA: this carries the co-applicant's FULL name ("JOINT CLIENT —
                      // NIVEN ABDALLAH MOHAMED SEIF EL…"), and uppercase + 0.08em tracking makes
                      // it wider still. `nowrap` ran it straight off the card, where an ancestor
                      // clipped it — so the name was cut mid-word rather than widening the page,
                      // which is why the overflow probe scored this surface clean. Let it wrap.
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {section.label}
                  </Typography>
                  {/* the rule still needs a sliver of width once the label takes two lines */}
                  <Divider sx={{ flex: 1, minWidth: 16 }} />
                </Stack>
              )}
              {section.steps.map((boardStep) => (
                <StepCard
                  key={boardStep.step.uid}
                  boardStep={boardStep}
                  expanded={expandedStepUid === boardStep.step.uid}
                  onToggle={() => toggleStep(boardStep.step.uid)}
                  caseUid={caseUid}
                  participantUid={participantUid}
                  customerUid={customerUid ?? client?.uid ?? null}
                  phaseBypass={activePhase?.bypass ?? null}
                />
              ))}
            </Box>
          ))}
          {!isSwitching && !activePhase?.steps.length && (
            <Typography variant="body2" color={colors.semantic.text.secondary}>
              No steps in this phase.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default NawyNowTab;
